import { useState, useEffect, useCallback } from 'react'
import { FiX } from 'react-icons/fi'
import { adminUsers, subscriptions, type AdminUserSummary, type AdminUserDetail, type AdminLoginSession } from '../api/client'
import PageHeader from './PageHeader'

/* ── User List Page ─────────────────────────────────────────── */

interface UsersListPageProps {
  onSelect: (id: string) => void
  onBack:   () => void
}

export function UsersListPage({ onSelect, onBack }: UsersListPageProps) {
  const [userList, setUserList]       = useState<AdminUserSummary[]>([])
  const [loading, setLoading]         = useState(true)
  const [search, setSearch]           = useState('')
  const [query, setQuery]             = useState('')
  const [flaggedOnly, setFlaggedOnly] = useState(false)

  const load = useCallback((email?: string, onlyFlagged?: boolean) => {
    return adminUsers.list(email || undefined, onlyFlagged)
      .then(setUserList)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load(undefined, flaggedOnly) }, [load, flaggedOnly])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setQuery(search.trim())
    setLoading(true)
    load(search.trim() || undefined, flaggedOnly)
  }

  const clearSearch = () => {
    setSearch('')
    setQuery('')
    setLoading(true)
    load(undefined, flaggedOnly)
  }

  const toggleFlagged = () => {
    const next = !flaggedOnly
    setFlaggedOnly(next)
    setSearch('')
    setQuery('')
    setLoading(true)
    load(undefined, next)
  }

  return (
    <div style={styles.page}>
      <PageHeader onBack={onBack} backLabel="← Admin" title="Usuarios" />

      <div style={styles.content}>
        {/* Search bar */}
        <form onSubmit={handleSearch} style={styles.searchRow}>
          <input
            style={styles.searchInput}
            type="email"
            placeholder="Buscar por email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoComplete="off"
          />
          <button type="submit" style={styles.searchBtn}>Buscar</button>
          {query && (
            <button type="button" onClick={clearSearch} style={styles.clearBtn}><FiX size={13} style={{ marginRight: 4 }} />Limpiar</button>
          )}
          <button
            type="button"
            onClick={toggleFlagged}
            style={{ ...styles.flagFilterBtn, ...(flaggedOnly ? styles.flagFilterActiveOverride : {}) }}
            title="Mostrar solo usuarios con sesiones sospechosas"
          >
            ⚠️ {flaggedOnly ? 'Solo sospechosos' : 'Todos'}
          </button>
        </form>

        {query && (
          <p style={styles.queryNote}>Resultados para: <strong>{query}</strong></p>
        )}

        {/* Table */}
        {loading ? (
          <p style={styles.emptyText}>Cargando…</p>
        ) : userList.length === 0 ? (
          <p style={styles.emptyText}>No se encontraron usuarios.</p>
        ) : (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Nombre</th>
                  <th style={styles.th}>Tier</th>
                  <th style={styles.th}>Rol</th>
                  <th style={styles.th}>Registro</th>
                </tr>
              </thead>
              <tbody>
                {userList.map((u) => (
                  <tr
                    key={u.id}
                    style={styles.tr}
                    onClick={() => onSelect(u.id)}
                  >
                    <td style={styles.td}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {u.flaggedMultiIp && (
                          <span style={styles.flagBadge} title={`Sospechoso${u.flaggedAt ? ` desde ${formatDate(u.flaggedAt)}` : ''}`}>⚠️</span>
                        )}
                        {u.email}
                      </span>
                    </td>
                    <td style={styles.td}>{u.displayName ?? <span style={styles.muted}>—</span>}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.tierBadge, ...tierColor(u.tier) }}>{u.tier}</span>
                    </td>
                    <td style={styles.td}>
                      {u.role === 'ADMIN'
                        ? <span style={styles.adminBadge}>ADMIN</span>
                        : <span style={styles.muted}>USER</span>}
                    </td>
                    <td style={{ ...styles.td, ...styles.muted }}>{formatDate(u.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

/* ── User Detail Page ───────────────────────────────────────── */

interface UserDetailPageProps {
  userId: string
  onBack: () => void
}

export function UserDetailPage({ userId, onBack }: UserDetailPageProps) {
  const [user, setUser]               = useState<AdminUserDetail | null>(null)
  const [loading, setLoading]         = useState(true)
  const [newTier, setNewTier]         = useState<'BASIC' | 'PLUS' | 'PREMIUM'>('BASIC')
  const [upgrading, setUpgrading]     = useState(false)
  const [upgradeMsg, setUpgradeMsg]   = useState<string | null>(null)
  const [clearingFlag, setClearingFlag]   = useState(false)
  const [clearFlagMsg, setClearFlagMsg]   = useState<string | null>(null)
  const [sessions, setSessions]           = useState<AdminLoginSession[]>([])

  const reloadSessions = useCallback(() => {
    adminUsers.sessions(userId).then(setSessions).catch(() => {})
  }, [userId])

  const reload = useCallback(() => {
    setLoading(true)
    adminUsers.get(userId)
      .then((u) => { setUser(u); setNewTier(u.tier) })
      .catch(() => {})
      .finally(() => setLoading(false))
    reloadSessions()
  }, [userId, reloadSessions])

  useEffect(() => { reload() }, [reload])

  const handleUpgrade = async () => {
    if (!user || newTier === user.tier) return
    if (!confirm(`¿Cambiar tier de "${user.email}" a ${newTier}?`)) return
    setUpgrading(true)
    setUpgradeMsg(null)
    try {
      await subscriptions.upgrade({ userId: user.id, tier: newTier })
      setUpgradeMsg(`Tier actualizado a ${newTier}`)
      reload()
    } catch (err: unknown) {
      setUpgradeMsg(err instanceof Error ? err.message : 'Error al actualizar')
    } finally {
      setUpgrading(false)
    }
  }

  const handleClearFlag = async () => {
    if (!user) return
    if (!confirm(`¿Limpiar la marca de sesión sospechosa para "${user.email}"?`)) return
    setClearingFlag(true)
    setClearFlagMsg(null)
    try {
      await adminUsers.clearFlag(user.id)
      setClearFlagMsg('Marca eliminada correctamente.')
      reload()
    } catch (err: unknown) {
      setClearFlagMsg(err instanceof Error ? err.message : 'Error al limpiar la marca')
    } finally {
      setClearingFlag(false)
    }
  }

  return (
    <div style={styles.page}>
      <PageHeader onBack={onBack} backLabel="← Usuarios" title="Detalle de usuario" />

      <div style={styles.content}>
        {loading ? (
          <p style={styles.emptyText}>Cargando…</p>
        ) : !user ? (
          <p style={styles.emptyText}>Usuario no encontrado.</p>
        ) : (
          <div style={styles.detailCard}>
            {/* Identity */}
            <section style={styles.section}>
              <h2 style={styles.sectionTitle}>Identidad</h2>
              <dl style={styles.dl}>
                <Row label="ID"    value={<code style={styles.code}>{user.id}</code>} />
                <Row label="Email" value={
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {user.email}
                    <span style={user.emailVerified ? styles.verifiedBadge : styles.unverifiedBadge}>
                      {user.emailVerified ? 'Verificado' : 'Sin verificar'}
                    </span>
                  </span>
                } />
                <Row label="Nombre" value={user.displayName ?? <span style={styles.muted}>—</span>} />
                <Row label="Idioma" value={user.preferredLanguage.toUpperCase()} />
                <Row label="Rol"
                  value={
                    user.role === 'ADMIN'
                      ? <span style={styles.adminBadge}>ADMIN</span>
                      : <span style={styles.muted}>USER</span>
                  }
                />
                <Row label="Miembro desde" value={formatDate(user.createdAt)} />
                <Row label="Última actualización" value={formatDate(user.updatedAt)} />
              </dl>
            </section>

            {/* Activity */}
            <section style={{ ...styles.section, marginTop: 32 }}>
              <h2 style={styles.sectionTitle}>Actividad</h2>
              <dl style={styles.dl}>
                <Row label="Sesiones de juego" value={String(user.gameSessionCount)} />
                <Row label="Última sesión"
                  value={user.lastSessionAt ? formatDate(user.lastSessionAt) : <span style={styles.muted}>Nunca</span>}
                />
              </dl>
            </section>

            {/* Security */}
            <section style={{ ...styles.section, marginTop: 32 }}>
              <h2 style={styles.sectionTitle}>Seguridad</h2>
              <dl style={styles.dl}>
                <Row label="Sesión sospechosa" value={
                  user.flaggedMultiIp
                    ? <span style={styles.flaggedBadge}>⚠️ Detectada{user.flaggedAt ? ` · ${formatDate(user.flaggedAt)}` : ''}</span>
                    : <span style={styles.verifiedBadge}>Sin alertas</span>
                } />
              </dl>
              {user.flaggedMultiIp && (
                <div style={{ marginTop: 16 }}>
                  <button
                    onClick={handleClearFlag}
                    disabled={clearingFlag}
                    style={clearingFlag ? styles.clearFlagBtnDisabled : styles.clearFlagBtn}
                  >
                    {clearingFlag ? 'Limpiando…' : '✓ Limpiar marca'}
                  </button>
                  {clearFlagMsg && (
                    <p style={clearFlagMsg.startsWith('Error') ? styles.errorText : styles.successText}>
                      {clearFlagMsg}
                    </p>
                  )}
                </div>
              )}

              {/* Active login sessions */}
              <h3 style={styles.subSectionTitle}>Sesiones activas</h3>
              {sessions.length === 0 ? (
                <p style={styles.muted}>Sin sesiones activas.</p>
              ) : (
                <div style={styles.sessionsTableWrap}>
                  <table style={styles.sessionsTable}>
                    <thead>
                      <tr>
                        <th style={styles.sessionsTh}>IP</th>
                        <th style={styles.sessionsTh}>Dispositivo</th>
                        <th style={styles.sessionsTh}>Inicio</th>
                        <th style={styles.sessionsTh}>Última actividad</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sessions.map((s) => (
                        <tr key={s.id} style={styles.sessionsTr}>
                          <td style={styles.sessionsTd}><code style={styles.code}>{s.ip}</code></td>
                          <td style={styles.sessionsTd}>{s.userAgent ? summariseUA(s.userAgent) : <span style={styles.muted}>—</span>}</td>
                          <td style={styles.sessionsTd}>{formatDate(s.createdAt)}</td>
                          <td style={styles.sessionsTd}>{formatDate(s.lastSeenAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Subscription */}
            <section style={{ ...styles.section, marginTop: 32 }}>
              <h2 style={styles.sectionTitle}>Suscripción</h2>
              <dl style={styles.dl}>
                <Row label="Tier actual"
                  value={<span style={{ ...styles.tierBadge, ...tierColor(user.tier) }}>{user.tier}</span>}
                />
                <Row label="Estado" value={user.subscriptionStatus} />
                <Row label="Activa" value={user.subscriptionIsActive ? 'Sí' : 'No'} />
                <Row label="Inicia" value={user.subscriptionStartsAt ? formatDate(user.subscriptionStartsAt) : <span style={styles.muted}>—</span>} />
                <Row label="Expira"
                  value={user.subscriptionExpiresAt ? formatDate(user.subscriptionExpiresAt) : <span style={styles.muted}>Nunca</span>}
                />
                <Row label="Stripe Customer"
                  value={user.stripeCustomerId
                    ? <a href={`https://dashboard.stripe.com/customers/${user.stripeCustomerId}`} target="_blank" rel="noopener noreferrer" style={styles.stripeLink}>{user.stripeCustomerId}</a>
                    : <span style={styles.muted}>—</span>}
                />
                <Row label="Stripe Subscription"
                  value={user.stripeSubscriptionId
                    ? <a href={`https://dashboard.stripe.com/subscriptions/${user.stripeSubscriptionId}`} target="_blank" rel="noopener noreferrer" style={styles.stripeLink}>{user.stripeSubscriptionId}</a>
                    : <span style={styles.muted}>—</span>}
                />
              </dl>

              {/* Tier upgrade */}
              <div style={styles.upgradeRow}>
                <label style={styles.upgradeLabel}>Cambiar tier:</label>
                <select
                  value={newTier}
                  onChange={(e) => setNewTier(e.target.value as 'BASIC' | 'PLUS' | 'PREMIUM')}
                  style={styles.tierSelect}
                >
                  <option value="BASIC">BASIC</option>
                  <option value="PLUS">PLUS</option>
                  <option value="PREMIUM">PREMIUM</option>
                </select>
                <button
                  onClick={handleUpgrade}
                  disabled={upgrading || newTier === user.tier}
                  style={newTier === user.tier ? styles.upgradeBtnDisabled : styles.upgradeBtn}
                >
                  {upgrading ? 'Actualizando…' : 'Actualizar'}
                </button>
              </div>
              {upgradeMsg && (
                <p style={upgradeMsg.startsWith('Error') ? styles.errorText : styles.successText}>
                  {upgradeMsg}
                </p>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  )
}

/* ── Helpers ─────────────────────────────────────────────────── */

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={rowStyles}>
      <dt style={dtStyle}>{label}</dt>
      <dd style={ddStyle}>{value}</dd>
    </div>
  )
}

const rowStyles: React.CSSProperties = { display: 'flex', gap: 16, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #F0E8DF' }
const dtStyle: React.CSSProperties = { minWidth: 140, fontWeight: 700, color: '#6B5744', fontSize: '0.9rem', margin: 0 }
const ddStyle: React.CSSProperties = { margin: 0, fontFamily: 'Nunito, sans-serif', fontSize: '0.95rem', color: '#2D1F14' }

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' })
}

function summariseUA(ua: string): string {
  const u = ua.toLowerCase()
  if (u.includes('iphone') || (u.includes('android') && u.includes('mobile'))) return 'Móvil'
  if (u.includes('ipad') || u.includes('tablet')) return 'Tablet'
  const browser =
    u.includes('chrome') ? 'Chrome' :
    u.includes('firefox') ? 'Firefox' :
    u.includes('safari') ? 'Safari' :
    u.includes('edg') ? 'Edge' : null
  return browser ? `Escritorio · ${browser}` : 'Escritorio'
}

function tierColor(tier: string): React.CSSProperties {
  if (tier === 'PREMIUM') return { background: '#7C3AED', color: '#FFFFFF' }
  if (tier === 'PLUS')    return { background: '#1B4332', color: '#FFFFFF' }
  return { background: '#E8DDD0', color: '#6B5744' }
}

/* ── Styles ──────────────────────────────────────────────────── */

const styles: Record<string, React.CSSProperties> = {
  page: { minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#FAF6EF' },

  content: {
    flex: 1,
    padding: '32px 24px',
    maxWidth: 900,
    width: '100%',
    margin: '0 auto',
    boxSizing: 'border-box',
  },

  searchRow: { display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' },
  searchInput: {
    flex: 1,
    minWidth: 220,
    padding: '10px 14px',
    fontSize: '1rem',
    fontFamily: 'Nunito, sans-serif',
    border: '2px solid #D4C4B0',
    borderRadius: 10,
    background: '#FFFFFF',
    color: '#2D1F14',
    outline: 'none',
  },
  searchBtn: {
    padding: '10px 20px',
    fontSize: '0.95rem',
    fontWeight: 700,
    fontFamily: 'Nunito, sans-serif',
    background: '#1B4332',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 10,
    cursor: 'pointer',
    minHeight: 44,
  },
  clearBtn: {
    padding:    '10px 14px',
    fontSize:   '0.9rem',
    fontWeight: 700,
    fontFamily: 'Nunito, sans-serif',
    background: 'transparent',
    color:      '#9A8070',
    border:     '2px solid #D4C4B0',
    borderRadius: 10,
    cursor:     'pointer',
    minHeight:  44,
    display:    'flex',
    alignItems: 'center',
  },
  queryNote: { fontSize: '0.88rem', color: '#9A8070', marginBottom: 16, fontFamily: 'Nunito, sans-serif' },

  tableWrap: { overflowX: 'auto' },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontFamily: 'Nunito, sans-serif',
    fontSize: '0.9rem',
    background: '#FFFFFF',
    borderRadius: 12,
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  },
  th: {
    textAlign: 'left',
    padding: '12px 16px',
    background: '#F5EDE4',
    color: '#6B5744',
    fontWeight: 700,
    fontSize: '0.875rem',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    borderBottom: '2px solid #E8DDD0',
  },
  tr: { cursor: 'pointer', borderBottom: '1px solid #F0E8DF' },
  td: { padding: '12px 16px', color: '#2D1F14', verticalAlign: 'middle' },
  muted: { color: '#9A8070', fontStyle: 'italic' },

  tierBadge: {
    display: 'inline-block',
    fontSize: '0.875rem',
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: 6,
    letterSpacing: '0.04em',
  },
  adminBadge: {
    display: 'inline-block',
    fontSize: '0.875rem',
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: 6,
    background: '#DC2626',
    color: '#FFFFFF',
    letterSpacing: '0.04em',
  },

  emptyText: { fontFamily: 'Nunito, sans-serif', color: '#9A8070', textAlign: 'center', marginTop: 48 },

  // Detail styles
  detailCard: {
    background: '#FFFFFF',
    borderRadius: 16,
    border: '2px solid #E8DDD0',
    padding: '32px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  },
  section: {},
  sectionTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: '1.3rem',
    fontWeight: 700,
    color: '#1B4332',
    margin: '0 0 16px',
  },
  dl: { margin: 0 },
  code: {
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    background: '#F5EDE4',
    padding: '2px 6px',
    borderRadius: 4,
    color: '#6B5744',
    wordBreak: 'break-all',
  },
  verifiedBadge: {
    display: 'inline-block',
    fontSize: '0.875rem',
    fontWeight: 700,
    padding: '1px 7px',
    borderRadius: 6,
    background: '#D1FAE5',
    color: '#065F46',
    letterSpacing: '0.03em',
  },
  unverifiedBadge: {
    display: 'inline-block',
    fontSize: '0.875rem',
    fontWeight: 700,
    padding: '1px 7px',
    borderRadius: 6,
    background: '#FEE2E2',
    color: '#991B1B',
    letterSpacing: '0.03em',
  },
  stripeLink: {
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    color: '#6366F1',
    textDecoration: 'none',
    wordBreak: 'break-all',
  },

  upgradeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginTop: 20,
    flexWrap: 'wrap',
  },
  upgradeLabel: {
    fontFamily: 'Nunito, sans-serif',
    fontWeight: 700,
    color: '#6B5744',
    fontSize: '0.9rem',
  },
  tierSelect: {
    padding: '8px 12px',
    fontSize: '0.95rem',
    fontFamily: 'Nunito, sans-serif',
    border: '2px solid #D4C4B0',
    borderRadius: 8,
    background: '#FFFFFF',
    color: '#2D1F14',
    cursor: 'pointer',
    outline: 'none',
  },
  upgradeBtn: {
    padding: '8px 20px',
    fontSize: '0.9rem',
    fontWeight: 700,
    fontFamily: 'Nunito, sans-serif',
    background: '#1B4332',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    minHeight: 40,
  },
  upgradeBtnDisabled: {
    padding: '8px 20px',
    fontSize: '0.9rem',
    fontWeight: 700,
    fontFamily: 'Nunito, sans-serif',
    background: '#D4C4B0',
    color: '#9A8070',
    border: 'none',
    borderRadius: 8,
    cursor: 'not-allowed',
    minHeight: 40,
  },
  errorText: {
    fontFamily: 'Nunito, sans-serif',
    fontSize: '0.9rem',
    color: '#DC2626',
    marginTop: 8,
  },
  successText: {
    fontFamily: 'Nunito, sans-serif',
    fontSize: '0.9rem',
    color: '#1B4332',
    fontWeight: 700,
    marginTop: 8,
  },

  flagBadge: {
    fontSize: '1rem',
    lineHeight: 1,
  },
  flaggedBadge: {
    display: 'inline-block',
    fontSize: '0.875rem',
    fontWeight: 700,
    padding: '2px 10px',
    borderRadius: 6,
    background: '#FEF3C7',
    color: '#92400E',
    border: '1px solid #F59E0B',
    letterSpacing: '0.02em',
  },
  flagFilterBtn: {
    padding: '10px 14px',
    fontSize: '0.9rem',
    fontWeight: 700,
    fontFamily: 'Nunito, sans-serif',
    background: 'transparent',
    color: '#9A8070',
    border: '2px solid #D4C4B0',
    borderRadius: 10,
    cursor: 'pointer',
    minHeight: 44,
    whiteSpace: 'nowrap' as const,
  },
  flagFilterActiveOverride: {
    background: '#FEF3C7',
    color: '#92400E',
    border: '2px solid #F59E0B',
  },
  clearFlagBtn: {
    padding: '8px 20px',
    fontSize: '0.9rem',
    fontWeight: 700,
    fontFamily: 'Nunito, sans-serif',
    background: '#1B4332',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    minHeight: 48,
  },
  clearFlagBtnDisabled: {
    padding: '8px 20px',
    fontSize: '0.9rem',
    fontWeight: 700,
    fontFamily: 'Nunito, sans-serif',
    background: '#D4C4B0',
    color: '#9A8070',
    border: 'none',
    borderRadius: 8,
    cursor: 'not-allowed',
    minHeight: 48,
  },

  subSectionTitle: {
    fontSize: '0.95rem',
    fontWeight: 700,
    fontFamily: 'Nunito, sans-serif',
    color: '#6B5744',
    marginTop: 28,
    marginBottom: 12,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
  },
  sessionsTableWrap: { overflowX: 'auto', marginTop: 4 },
  sessionsTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontFamily: 'Nunito, sans-serif',
    fontSize: '0.875rem',
    background: '#FAF6EF',
    borderRadius: 8,
    overflow: 'hidden',
    border: '1px solid #E8DDD0',
  },
  sessionsTh: {
    textAlign: 'left' as const,
    padding: '8px 12px',
    background: '#F0E8DC',
    color: '#6B5744',
    fontWeight: 700,
    fontSize: '0.875rem',
    borderBottom: '1px solid #E8DDD0',
  },
  sessionsTr: { borderBottom: '1px solid #F0E8DC' },
  sessionsTd: { padding: '8px 12px', color: '#2D1F14', verticalAlign: 'top' as const },
}
